/**
 * `/lists/:id` — members and share state (design §2.2).
 *
 * The share affordance here is the LIST grant, which is a different object from
 * a contact grant and is deliberately not conflated with one: `shares.list.*`
 * writes a row against the list, and the scope applies to every member it
 * exposes. Design §2.10 also describes a `view` / `contribute` distinction and
 * an "include member contact details" toggle; `listShareSchema` has neither —
 * it carries the same three-rung `shareScope` a contact grant does — so the
 * dialog offers the rungs that exist rather than inventing two more.
 */

import { Badge, Button, EmptyState, SkeletonRows } from '@crm/ui';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Share2Icon } from 'lucide-react';
import { useState } from 'react';
import { useListQuery } from '../../../api/queries.ts';
import { useOutgoingSharesQuery, useRevokeShareMutation } from '../../../api/sharing.ts';
import { ContactTypeBadge } from '../../../components/contacts/contact-type-badge.tsx';
import { NotWired } from '../../../components/not-wired.tsx';
import { PageBody, PageHeader } from '../../../components/page.tsx';
import { RouteErrorPanel } from '../../../components/route-error-boundary.tsx';
import { GrantShareDialog } from '../../../components/sharing/grant-share-dialog.tsx';
import { ShareChip } from '../../../components/sharing/share-chip.tsx';
import { scopeLabel } from '../../../components/sharing/share-scope.ts';
import { fullName } from '../../../lib/format.ts';

export const Route = createFileRoute('/_authed/lists/$listId')({
  component: ListDetailPage,
  errorComponent: RouteErrorPanel,
});

/**
 * This list's outgoing grants.
 *
 * Same shape as the contact panel's: `sharesOutgoingInputSchema` has no
 * `resourceId`, so the org's list grants are read and filtered here, and the
 * footer says so when the page was full rather than implying the list is
 * complete.
 */
function ListShareState({ listId, listName }: { listId: string; listName: string }) {
  const [granting, setGranting] = useState(false);
  const outgoing = useOutgoingSharesQuery({ resourceType: 'list' });
  const revoke = useRevokeShareMutation();

  const rows = (outgoing.data?.items ?? []).filter(
    (share) => share.resourceId === listId && share.revokedAt === null,
  );
  const mayBePartial = outgoing.data?.nextCursor != null;

  return (
    <section
      aria-labelledby="list-sharing-heading"
      className="rounded-lg border border-border p-4 text-sm"
    >
      <div className="flex items-center gap-2">
        <h2 id="list-sharing-heading" className="font-medium">
          Sharing
        </h2>
        <Button size="sm" variant="outline" className="ml-auto" onClick={() => setGranting(true)}>
          <Share2Icon aria-hidden className="size-3.5" />
          Share this list
        </Button>
      </div>

      {outgoing.isLoading ? (
        <SkeletonRows rows={2} className="mt-3" />
      ) : outgoing.isError ? (
        <p className="mt-2 text-muted-foreground">Could not load this list’s grants.</p>
      ) : rows.length === 0 ? (
        <p className="mt-2 text-muted-foreground">
          Not shared with anyone{mayBePartial ? ' on the first page of grants' : ''}.
        </p>
      ) : (
        <ul className="mt-3 flex flex-col gap-1.5">
          {rows.map((share) => (
            <li key={share.id} className="flex flex-wrap items-center gap-2">
              <span>
                {share.granteeType === 'org'
                  ? (share.granteeOrgName ?? share.granteeOrgId)
                  : (share.granteeUserEmail ?? share.granteeUserId)}
              </span>
              <Badge variant="secondary">{scopeLabel(share.scope)}</Badge>
              <span className="text-xs text-muted-foreground">
                exposes {share.contactCount} contact{share.contactCount === 1 ? '' : 's'}
              </span>
              <Button
                size="sm"
                variant="ghost"
                className="ml-auto text-destructive"
                disabled={revoke.isPending}
                onClick={() => revoke.mutate({ shareId: share.id, resourceType: 'list' })}
              >
                Revoke
              </Button>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-3 text-xs text-muted-foreground">
        A list grant exposes its members at the chosen scope. Adding someone to a shared list shares
        them too — that is what makes list sharing useful, and what makes it worth checking before
        you add.
      </p>

      {granting ? (
        <GrantShareDialog
          open
          onOpenChange={setGranting}
          resourceType="list"
          resourceId={listId}
          resourceLabel={listName}
        />
      ) : null}
    </section>
  );
}

function ListDetailPage() {
  const { listId } = Route.useParams();
  const list = useListQuery(listId);

  if (list.isLoading) {
    return <SkeletonRows rows={6} className="p-4" />;
  }
  if (list.isError || list.data === undefined) {
    return <EmptyState kind="error" className="m-4" title="Could not load this list" />;
  }

  const detail = list.data;

  return (
    <>
      <PageHeader
        title={detail.name}
        description={detail.description ?? `${detail.itemCount} contacts · ${detail.kind} list`}
        actions={<Badge variant="muted">{detail.visibility}</Badge>}
      />
      <PageBody>
        <ListShareState listId={listId} listName={detail.name} />

        {detail.members.length === 0 ? (
          <EmptyState title="This list has no members yet" />
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {detail.members.map((member) => (
              <li key={member.id}>
                <Link
                  to="/contacts/$contactId"
                  params={{ contactId: member.id }}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-accent/50"
                >
                  <span className="font-medium">{fullName(member.firstName, member.lastName)}</span>
                  <ContactTypeBadge type={member.contactType} />
                  <ShareChip
                    compact
                    source={member.access.source}
                    ownerOrgName={member.access.ownerOrgName}
                    scope={member.access.scope}
                  />
                  <span className="ml-auto text-xs text-muted-foreground">
                    {member.companyName ?? ''}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}

        <NotWired
          title="Member paging, add/remove"
          blockedOn="Only the first page of members is fetched; lists.items.add / .remove are not wired in this track"
        >
          Design §2.10’s <code>view</code>/<code>contribute</code> permission and the “include
          member contact details” toggle are not in <code>listShareSchema</code> either — a list
          grant carries the same three-rung scope a contact grant does, and that is what the share
          dialog offers.
        </NotWired>
      </PageBody>
    </>
  );
}
